import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, TextField, MenuItem, Chip, LinearProgress
} from "@mui/material";
import { Download, TrendingUp } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ORIGIN as API_URL } from "../../config/env";
import DateRangeFilter, { buildDateRangeParams, defaultDateRangeFilter } from "../../components/common/DateRangeFilter";
import { AdminAction } from "../../components/admin";

export default function VideoPerformanceDashboard() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(defaultDateRangeFilter);

  const token = localStorage.getItem("rfis");

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (languageFilter) params.language = languageFilter;
      if (categoryFilter) params.category = categoryFilter;
      Object.assign(params, buildDateRangeParams(dateFilter));

      const res = await axios.get(`${API_URL}/api/v1/video-interactions/admin/performance`, {
        headers: { rf_token_header123_k: token },
        params,
      });
      setVideos(res.data.data);
    } catch (err) {
      toast.error("Failed to fetch performance data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [languageFilter, categoryFilter, dateFilter]);

  const handleExport = () => {
    const headers = "ID,Title,Category,Language,Total Views,Unique Views,Avg Watch Duration (s)\n";
    const csv = videos.map((v) =>
      `${v.id},"${v.title}",${v.category},${v.language},${v.totalViews || 0},${v.uniqueViews || 0},${Math.round(v.avgWatchDuration || 0)}`
    ).join("\n");

    const blob = new Blob([headers + csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "video_performance_report.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported!");
  };

  const languages = ["Hindi", "English", "Tamil", "Gujarati", "Marathi", "Kannada", "Telugu", "Odia", "Malayalam", "Bangla"];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingUp color="primary" />
          <Typography variant="h5" fontWeight="bold">
            Video Performance Dashboard
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
          <AdminAction variant="outline" startIcon={<Download />} onClick={handleExport}>
            Export CSV
          </AdminAction>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          select
          label="Language"
          value={languageFilter}
          onChange={(e) => setLanguageFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All (PAN India)</MenuItem>
          {languages.map((lang) => (
            <MenuItem key={lang} value={lang}>{lang}</MenuItem>
          ))}
        </TextField>
        {/* <TextField
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          size="small"
          placeholder="Filter by category"
        /> */}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Rank</strong></TableCell>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Language</strong></TableCell>
              <TableCell align="center"><strong>Total Views</strong></TableCell>
              <TableCell align="center"><strong>Unique Views</strong></TableCell>
              <TableCell align="center"><strong>Avg Watch (sec)</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video, idx) => (
              <TableRow key={video.id}>
                <TableCell>
                  {idx < 3 ? (
                    <Chip label={`#${idx + 1}`} color={idx === 0 ? "success" : idx === 1 ? "primary" : "warning"} size="small" />
                  ) : (
                    idx + 1
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {video.title}
                </TableCell>
                <TableCell>{video.category || "N/A"}</TableCell>
                <TableCell>{video.language || "N/A"}</TableCell>
                <TableCell align="center"><strong>{video.totalViews || 0}</strong></TableCell>
                <TableCell align="center">{video.uniqueViews || 0}</TableCell>
                <TableCell align="center">{Math.round(video.avgWatchDuration || 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
